import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Search, X, Clock, TrendingUp } from 'lucide-react';
import { foodCommunityService } from '../services/foodCommunityService';
import { RecipePostCard } from '../components/food-community/RecipePostCard';

const POPULAR_SEARCHES = [
  'highprotein', 'vegan', 'slăbit', 'masă musculară',
  'anti-inflamator', 'fără gluten', 'salată', 'smoothie',
];

const FoodCommunitySearchPage: React.FC = () => {
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [results, setResults] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [recentSearches, setRecentSearches] = useState<string[]>(() => {
    try {
      return JSON.parse(localStorage.getItem('fc_recent_searches') || '[]');
    } catch {
      return [];
    }
  });

  // Focus input on mount
  useEffect(() => {
    setTimeout(() => inputRef.current?.focus(), 100);
  }, []);

  // Debounce query
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQuery(query), 400);
    return () => clearTimeout(timer);
  }, [query]);

  // Search when debounced query changes
  useEffect(() => {
    if (!debouncedQuery.trim()) {
      setResults([]);
      setTotal(0);
      return;
    }
    doSearch(debouncedQuery.trim());
  }, [debouncedQuery]);

  const doSearch = useCallback(async (q: string) => {
    setLoading(true);
    try {
      const res = await foodCommunityService.search(q, 1, 20);
      setResults(res.posts || []);
      setTotal(res.pagination?.total || res.posts?.length || 0);
    } catch (err) {
      setResults([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleSearch = (q: string) => {
    if (!q.trim()) return;
    setQuery(q);
    // Save to recent
    const updated = [q, ...recentSearches.filter(s => s !== q)].slice(0, 8);
    setRecentSearches(updated);
    localStorage.setItem('fc_recent_searches', JSON.stringify(updated));
  };

  const clearRecent = () => {
    setRecentSearches([]);
    localStorage.removeItem('fc_recent_searches');
  };

  const handlePostUpdate = (updated: any) => {
    setResults(prev => prev.map(p => p.id === updated.id ? updated : p));
  };

  const showEmpty = !query.trim();

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-100 sticky top-0 z-30">
        <div className="max-w-2xl mx-auto px-4 py-3 flex items-center gap-3">
          <button
            onClick={() => navigate(-1)}
            className="w-9 h-9 rounded-full bg-gray-100 flex items-center justify-center hover:bg-gray-200 transition-colors flex-shrink-0"
          >
            <ArrowLeft size={18} className="text-gray-600" />
          </button>

          <div className="flex-1 relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={e => setQuery(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleSearch(query)}
              placeholder="Cauta retete, ingrediente, taguri..."
              className="w-full pl-9 pr-9 py-2.5 bg-gray-100 rounded-xl text-sm text-gray-900 placeholder-gray-400 outline-none focus:bg-gray-50 focus:ring-2 focus:ring-green-500 transition-all"
            />
            {query && (
              <button
                onClick={() => { setQuery(''); setResults([]); setTotal(0); }}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <X size={15} />
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-4">
        {showEmpty ? (
          <>
            {/* Recent searches */}
            {recentSearches.length > 0 && (
              <div className="mb-6">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <Clock size={15} className="text-gray-400" />
                    <span className="text-sm font-semibold text-gray-700">Cautari recente</span>
                  </div>
                  <button
                    onClick={clearRecent}
                    className="text-xs text-gray-400 hover:text-gray-600"
                  >
                    Sterge tot
                  </button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {recentSearches.map(s => (
                    <button
                      key={s}
                      onClick={() => handleSearch(s)}
                      className="px-3 py-1.5 bg-white border border-gray-200 rounded-full text-sm text-gray-700 hover:bg-gray-50 hover:border-gray-300 transition-colors"
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Popular searches */}
            <div>
              <div className="flex items-center gap-2 mb-3">
                <TrendingUp size={15} className="text-green-600" />
                <span className="text-sm font-semibold text-gray-700">Cautari populare</span>
              </div>
              <div className="flex flex-wrap gap-2">
                {POPULAR_SEARCHES.map(s => (
                  <button
                    key={s}
                    onClick={() => handleSearch(s)}
                    className="px-3 py-1.5 bg-green-50 border border-green-100 rounded-full text-sm text-green-700 hover:bg-green-100 transition-colors font-medium"
                  >
                    #{s}
                  </button>
                ))}
              </div>
            </div>
          </>
        ) : loading ? (
          <div className="space-y-4">
            {[1, 2, 3].map(i => (
              <div key={i} className="bg-white rounded-2xl border border-gray-100 overflow-hidden animate-pulse">
                <div className="flex items-center gap-3 p-4">
                  <div className="w-10 h-10 rounded-full bg-gray-200" />
                  <div className="flex-1">
                    <div className="h-3 bg-gray-200 rounded w-32 mb-1.5" />
                    <div className="h-2.5 bg-gray-100 rounded w-20" />
                  </div>
                </div>
                <div className="h-48 bg-gray-200" />
                <div className="p-4 space-y-2">
                  <div className="h-4 bg-gray-200 rounded w-3/4" />
                  <div className="h-3 bg-gray-100 rounded w-full" />
                </div>
              </div>
            ))}
          </div>
        ) : results.length === 0 ? (
          <div className="text-center py-16">
            <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-4">
              <Search size={28} className="text-gray-300" />
            </div>
            <h3 className="font-semibold text-gray-700 mb-1">Niciun rezultat</h3>
            <p className="text-sm text-gray-400">
              Nu am gasit retete pentru "{query}"
            </p>
          </div>
        ) : (
          <>
            <p className="text-xs text-gray-400 mb-3 font-medium">
              {total} {total === 1 ? 'rezultat' : 'rezultate'} pentru "{query}"
            </p>
            <div className="space-y-0">
              {results.map(post => (
                <RecipePostCard
                  key={post.id}
                  post={post}
                  onUpdate={handlePostUpdate}
                />
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default FoodCommunitySearchPage;
